/**
 * Authentication utilities for Bona Pisca
 * Standardized auth checking and validation functions
 */

import { AuthUser } from 'aws-amplify/auth';
import i18next from 'i18next';
import { isAccountRoute } from '../config/accessModel';
import { PUBLIC_ACCOUNT_FEATURES_ENABLED } from '../config/publicFeatures';

// Type definitions for auth state
export interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  isLoading: boolean;
}

export type LoginIntent = 'save_route' | 'save_plan' | 'saved_plans' | 'favorite_species' | 'favorite_technique' | 'favorite_location' | 'favorite_rig';

const tAuthUtils = (key: string, options?: Record<string, unknown>) =>
  String(i18next.t(`auth:utils.${key}`, options));

export const buildLoginRedirectPath = (from?: string, intent?: LoginIntent): string => {
  if (!PUBLIC_ACCOUNT_FEATURES_ENABLED) {
    return from && from !== '/login' ? from : '/';
  }

  const params = new URLSearchParams();

  if (from && from !== '/login') {
    params.set('returnTo', from);
  }

  if (intent) {
    params.set('intent', intent);
  }

  const query = params.toString();
  return query ? `/login?${query}` : '/login';
};

// Helper functions for auth validation
export const authUtils = {
  /**
   * Check if user is fully authenticated with valid user object
   */
  isUserLoggedIn: (state: AuthState): boolean => {
    return state.isAuthenticated && !!state.user && !state.isLoading;
  },

  /**
   * Check if user is definitively logged out
   */
  isUserLoggedOut: (state: AuthState): boolean => {
    return !state.isAuthenticated && !state.user && !state.isLoading;
  },

  /**
   * Check if auth status is still being determined
   */
  isAuthPending: (state: AuthState): boolean => {
    return state.isLoading;
  },

  /**
   * Get user display name with fallback
   */
  getUserDisplayName: (user: AuthUser | null): string => {
    if (!user) return tAuthUtils('fallbackDisplayName');
    
    // Try to get user's email or username
    return user.signInDetails?.loginId || tAuthUtils('fallbackDisplayName');
  },

  /**
   * Get user email if available
   */
  getUserEmail: (user: AuthUser | null): string | null => {
    if (!user) return null;
    return user.signInDetails?.loginId || null;
  },

  /**
   * Check if authentication is required for a route
   */
  isProtectedRoute: (pathname: string): boolean => {
    return isAccountRoute(pathname);
  },

  /**
   * Get redirect path after login
   */
  getRedirectPath: (from?: string): string => {
    // If user was trying to access a protected route, redirect there
    if (from && authUtils.isProtectedRoute(from)) {
      return from;
    }
    // Default redirect to the personal surface that actually requires auth
    return '/logbook';
  },

  /**
   * Format user for logging (remove sensitive data)
   */
  sanitizeUserForLogging: (user: AuthUser | null) => {
    if (!user) return null;
    
    return {
      userId: user.userId,
      username: user.username,
      signInDetails: user.signInDetails ? {
        loginId: user.signInDetails.loginId
      } : null
    };
  },

  /**
   * Debug authentication state - safe for logging
   */
  debugAuthState: (state: AuthState, context = 'Unknown') => {
    const debugInfo = {
      context,
      isAuthenticated: state.isAuthenticated,
      hasUser: !!state.user,
      isLoading: state.isLoading,
      user: authUtils.sanitizeUserForLogging(state.user),
      timestamp: new Date().toISOString()
    };
    
    console.log(`🔐 Auth Debug [${context}]:`, debugInfo);
    return debugInfo;
  }
};

// Constants for auth-related values
export const AUTH_CONSTANTS = {
  // Routes
  LOGIN_ROUTE: '/login',
  HOME_ROUTE: '/',
  DEFAULT_PROTECTED_ROUTE: '/logbook',
  
  // Storage keys
  AUTH_STORAGE_KEY: 'auth-storage',
  
  // Timeouts
  AUTH_CHECK_TIMEOUT: 10000, // 10 seconds
  
  // Messages
  MESSAGES: {
    CHECKING_AUTH: tAuthUtils('messages.checkingAuth'),
    LOGIN_REQUIRED: tAuthUtils('messages.loginRequired'),
    AUTH_EXPIRED: tAuthUtils('messages.authExpired'),
    LOADING: tAuthUtils('messages.loading')
  }
} as const;

export default authUtils;
